import { Center, Grid, GridItem, Icon, Tab, TabList, TabPanel, TabPanels, Tabs, Text } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FaCubes, FaFileSignature, FaFolder, FaInfoCircle, FaShoppingBasket } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { ProjectDto, ProjectPatchDto, useGetProjectQuery, useUpdateProjectMutation } from "../../../autogen/bff-api";
import { useCreateEditor } from "../../../common/editing/save-coordinator";
import { EntityViewSkeleton } from "../../../common/EntityViewSkeleton";
import { EntityUnavailable } from "../../../common/errors/EntityUnavailable";
import { useApiError } from "../../../common/errors/useApiError";
import { Layout } from "../../../common/layout/Layout";
import { useSub } from "../../../common/subscription/useSub";
import { ProjectTab, useQueryParams } from "../../../common/useQueryParams";
import { CONTENT_WIDTH } from "../../../config";
import { urls } from "../../../urls";
import { Contracts } from "./Contracts";
import { Documents } from "./Documents";
import { Products } from "./products/Products";
import { ProjectDetails } from "./ProjectDetails";
import { ProjectHeading } from "./ProjectHeading";
import { SourcingEvents } from "./SourcingEvents";

const tabs: ProjectTab[] = ["details", "documents", "products", "sourcing-events", "contracts"];
const tabToIndex = (tab: ProjectTab): number => tabs.indexOf(tab);
const indexToQueryValue = (index: number): ProjectTab => tabs[index];

const EmbeddedWrapper = ({ children }: { children: React.ReactNode }) => <>{children}</>;

const toProjectPatch = (fields: Partial<ProjectDto>): ProjectPatchDto => ({
  ...("externalId" in fields && { externalId: fields.externalId }),
  ...("name" in fields && { name: fields.name }),
  ...("description" in fields && { description: fields.description }),
  ...("startDate" in fields && { startDate: fields.startDate }),
  ...("endDate" in fields && { endDate: fields.endDate }),
  ...("parent" in fields && { parentId: fields.parent?.id ?? null }),
  ...("projectResponsible" in fields && { projectResponsibleId: fields.projectResponsible?.id ?? null }),
  ...("sourcingResponsible" in fields && { sourcingResponsibleId: fields.sourcingResponsible?.id ?? null }),
  ...("projectType" in fields && { projectTypeId: fields.projectType?.id ?? null }),
  ...("documents" in fields && { documents: fields.documents ?? [] }),
  ...("descriptionDocuments" in fields && { descriptionDocuments: fields.descriptionDocuments ?? [] }),
});

const LoadedProjectContent = ({ project, refetch }: { project: ProjectDto; refetch: () => void }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const params = useQueryParams();
  const displayer = useApiError();
  const [updateProject] = useUpdateProjectMutation();

  useEffect(() => {
    const tab = params.getProjectTab();
    if (!tab) return;
    setTabIndex(tabToIndex(tab));
  }, [params]);

  const save = useCallback(
    async (fields: Partial<ProjectDto>) =>
      await updateProject({ projectId: project.id, projectPatchDto: toProjectPatch(fields) }).unwrap(),
    [project.id, updateProject]
  );

  const config = useMemo(
    () => ({ initialState: project, save, onError: displayer.trigger }),
    [project, save, displayer.trigger]
  );

  useCreateEditor<ProjectDto>(project.id, config);

  return (
    <>
      <ProjectHeading projectId={project.id} />
      <GridItem area="content" display="flex" flexDirection="column" pt="6">
        <Tabs
          colorScheme="cyan"
          index={tabIndex}
          onChange={(index) => {
            setTabIndex(index);
            params.setSelectedProjectTab(indexToQueryValue(index));
          }}
        >
          <TabList>
            <Tab>
              <Icon as={FaInfoCircle} w="15px" h="15px" mr="2.5" />
              {t("Details")}
            </Tab>
            <Tab>
              <Icon as={FaFolder} w="15px" h="15px" mr="10px" />
              {t("Documents")}
            </Tab>
            <Tab>
              <Icon as={FaCubes} w="15px" h="15px" mr="10px" />
              {t("Products")}
            </Tab>
            <Tab>
              <Icon as={FaShoppingBasket} w="15px" h="15px" mr="10px" />
              {t("Sourcing Events")}
            </Tab>
            <Tab>
              <Icon as={FaFileSignature} w="15px" h="15px" mr="10px" />
              {t("Contracts")}
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel p="5">
              <ProjectDetails projectId={project.id} />
            </TabPanel>
            <TabPanel>
              <Documents projectId={project.id} refetch={refetch} />
            </TabPanel>
            <TabPanel>
              <Products project={project} />
            </TabPanel>
            <TabPanel>
              <SourcingEvents project={project} />
            </TabPanel>
            <TabPanel>
              <Contracts project={project} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </GridItem>
    </>
  );
};

export const ProjectPage = ({ embedded = false }: { embedded?: boolean } = {}) => {
  const { projectId } = useParams();
  if (!projectId) throw new Error("No project id");
  const sub = useSub();

  const noAccess = embedded && !sub.hasProjectAccess;
  const { data: project, error, isLoading, isFetching, refetch } = useGetProjectQuery(
    noAccess ? skipToken : { projectId }
  );

  const Wrapper = embedded ? EmbeddedWrapper : Layout;

  if (noAccess) {
    return (
      <Wrapper>
        <Center py="20">
          <Text color="smMuted">{t("Your organization does not have access to this feature.")}</Text>
        </Center>
      </Wrapper>
    );
  }

  if (error) {
    return (
      <Wrapper>
        <EntityUnavailable
          error={error}
          notFoundMessage={t("This project doesn't exist, or your organization doesn't have access to it.")}
          forbiddenMessage={t("You don't have access to this project. Ask an administrator in your organization for access.")}
          loadFailedMessage={t("This project could not be loaded right now.")}
          onRetry={refetch}
          isRetrying={isFetching}
          back={{ to: embedded ? urls.v2.projects.viewAll : urls.projects.viewAll, label: t("Back to projects") }}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Grid
        height="full"
        gridTemplateColumns={`1fr minmax(0, ${CONTENT_WIDTH}) 1fr`}
        gridTemplateRows={"minmax(0, 1fr)"}
        templateAreas={`"leftContent content rightContent"`}
      >
        <GridItem height="full" area={"content"}>
          <Grid
            width="full"
            gridTemplateColumns={"3fr 4fr 250px"}
            gridTemplateRows="minmax(50px,1fr) auto auto auto auto minmax(0,1fr)"
            templateAreas={`
                        "title title edit"
                        "createdMeta createdMeta edit"
                        "deadline deadline deadline"
                        "type type type"
                        "content content content"
                        `}
          >
            {(isLoading || (isFetching && project.id !== projectId)) && <EntityViewSkeleton />}
            {project && project.id === projectId && <LoadedProjectContent project={project} refetch={refetch} />}
          </Grid>
        </GridItem>
      </Grid>
    </Wrapper>
  );
};
